/**
 * TextDisplayer - Composant d'affichage de texte optimisé pour les performances
 *
 * Optimisations implémentées :
 * - Mémorisation avec React.memo et comparaison personnalisée
 * - Calcul de hauteur optimisé avec requestAnimationFrame
 * - Utilisation de div au lieu de textarea pour de meilleures performances
 * - Gestion intelligente du redimensionnement
 */
import React, { useEffect, useRef, useCallback, useMemo } from "react";
import "./TextDisplayer.styles.scss";
import classNames from "classnames";

type TTextDisplayerProps = {
  value: string;
  rows?: number;
  className?: string;
  style?: React.CSSProperties | undefined;
  forMessage?: boolean;
  maxLength?: number;
  onMouseEnter?: React.MouseEventHandler<HTMLTextAreaElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLTextAreaElement>;
  isForPopper?: boolean;
};

const TextDisplayer: React.FC<TTextDisplayerProps> = React.memo(
  ({
    value,
    rows,
    className = "",
    style,
    forMessage = false,
    maxLength,
    onMouseEnter,
    onMouseLeave,
    isForPopper = false,
  }) => {
    const elementRef = useRef<HTMLDivElement | HTMLTextAreaElement>(null);

    // Mémorisation du contenu avec optimisations pour les textes longs
    const displayValue = useMemo(() => {
      if (!value) return "";

      if (maxLength && value.length > maxLength) {
        return value.substring(0, maxLength) + "...";
      }

      // Pour les très longs textes, optimiser l'affichage
      if (value.length > 10000 && !isForPopper) {
        return (
          value.substring(0, 5000) +
          "\n\n... (texte tronqué pour optimiser les performances) ..."
        );
      }

      return value;
    }, [value, maxLength, isForPopper]);

    // Mémorisation des classes CSS
    const cssClasses = useMemo(
      () => classNames("c-text-displayer", className),
      [className]
    );

    // Style stable sans optimisations agressives qui causent des clignotements
    const combinedStyle = useMemo(
      () => ({
        ...style,
        // Optimisations légères uniquement
        backfaceVisibility: "hidden" as const,
        WebkitBackfaceVisibility: "hidden" as const,
        // Pas de contain ou willChange qui peuvent causer des problèmes
      }),
      [style]
    );

    // Calcul de hauteur stabilisé (sans requestAnimationFrame pour éviter les clignotements)
    const adjustHeight = useCallback(() => {
      if (!elementRef.current) return;

      const element = elementRef.current;

      try {
        if (element instanceof HTMLTextAreaElement) {
          // Pour les textarea - calcul simple et stable
          const currentHeight = element.style.height;

          // Mesurer le contenu sans animation
          element.style.height = "auto";
          let newHeight = element.scrollHeight;

          // Ajouter padding selon le contexte
          newHeight += isForPopper ? 30 : 10;

          // Hauteur minimale
          newHeight = Math.max(newHeight, 40);
          const newHeightPx = `${newHeight}px`;

          // Appliquer seulement si vraiment différent
          if (currentHeight !== newHeightPx) {
            element.style.height = newHeightPx;
            element.style.overflowY = newHeight > 200 ? "auto" : "hidden";
          }
        }
        // Pour les div, laisser le CSS gérer naturellement (pas de manipulation)
      } catch (error) {
        console.warn("Erreur hauteur:", error);
      }
    }, [isForPopper]);

    // Ajustement de hauteur simplifié et stabilisé
    useEffect(() => {
      // Un seul appel avec délai minimal pour éviter les clignotements
      const timeoutId = setTimeout(() => {
        adjustHeight();
      }, 50); // Délai unique plus long pour stabilité

      return () => clearTimeout(timeoutId);
    }, [value, adjustHeight]); // Seulement quand value change

    // Handlers optimisés
    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLDivElement | HTMLTextAreaElement>) => {
        onMouseEnter?.(e as React.MouseEvent<HTMLTextAreaElement>);
      },
      [onMouseEnter]
    );

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLDivElement | HTMLTextAreaElement>) => {
        onMouseLeave?.(e as React.MouseEvent<HTMLTextAreaElement>);
      },
      [onMouseLeave]
    );

    // Pour les messages simples, utiliser un div avec formatage préservé
    if (forMessage && !isForPopper) {
      return (
        <div
          className={cssClasses}
          style={{
            ...combinedStyle,
            // Formatage simple et stable
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
            // Hauteur naturelle sans manipulation
            minHeight: "1.2em",
          }}
          ref={elementRef as React.RefObject<HTMLDivElement>}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {displayValue}
        </div>
      );
    }

    // Pour les cas complexes (popper, etc.), garder textarea avec hauteur stable
    return (
      <textarea
        className={cssClasses}
        style={{
          ...combinedStyle,
          // Style stable pour textarea
          resize: "none",
          overflow: "hidden",
          // minHeight: "40px",
          // Formatage simple
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
        }}
        readOnly
        ref={elementRef as React.RefObject<HTMLTextAreaElement>}
        rows={rows}
        value={displayValue}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />
    );
  },
  (prevProps, nextProps) => {
    // Comparaison personnalisée optimisée pour éviter les re-renders inutiles
    if (
      prevProps.value !== nextProps.value ||
      prevProps.className !== nextProps.className ||
      prevProps.isForPopper !== nextProps.isForPopper ||
      prevProps.forMessage !== nextProps.forMessage ||
      prevProps.maxLength !== nextProps.maxLength ||
      prevProps.rows !== nextProps.rows
    ) {
      return false;
    }

    // Comparaison optimisée des styles (évite JSON.stringify)
    const prevStyle = prevProps.style || {};
    const nextStyle = nextProps.style || {};
    const prevKeys = Object.keys(prevStyle);
    const nextKeys = Object.keys(nextStyle);

    if (prevKeys.length !== nextKeys.length) return false;

    for (const key of prevKeys) {
      if (
        prevStyle[key as keyof React.CSSProperties] !==
        nextStyle[key as keyof React.CSSProperties]
      ) {
        return false;
      }
    }

    // Comparaison des handlers (référence uniquement)
    return (
      prevProps.onMouseEnter === nextProps.onMouseEnter &&
      prevProps.onMouseLeave === nextProps.onMouseLeave
    );
  }
);

export default TextDisplayer;
